import { Box, Tooltip, type TooltipProps } from "@mui/material";
import type { ReactNode } from "react";
import { tokens } from "../theme";

interface HelpBadgeProps {
  /** The explanation shown on hover / focus / tap. */
  title: ReactNode;
  /**
   * Absolutely pin it to the top-right corner of a positioned parent. The
   * parent card needs `position: relative`. Default true.
   */
  corner?: boolean;
  /** Tooltip placement. Default "left" so a corner badge opens inward. */
  placement?: TooltipProps["placement"];
  /** Accessible label for the trigger. Default "More information". */
  ariaLabel?: string;
}

/**
 * A small circular "?" badge that reveals an explanation on hover / focus /
 * tap. Meant to sit in the top-right corner of a card so help is available on
 * demand without cluttering the surface with permanent description text.
 */
export default function HelpBadge({
  title,
  corner = true,
  placement = "left",
  ariaLabel = "More information",
}: HelpBadgeProps) {
  return (
    <Tooltip
      title={title}
      placement={placement}
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={4000}
      slotProps={{ tooltip: { sx: { maxWidth: 280 } } }}
    >
      <Box
        component="span"
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        sx={{
          ...(corner && {
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 1,
          }),
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          borderRadius: "50%",
          flexShrink: 0,
          cursor: "help",
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1,
          userSelect: "none",
          color: tokens.brandStrong,
          bgcolor: tokens.brandTint,
          border: "1px solid",
          borderColor: tokens.brand,
          // The "round thing around it" — a soft brand halo ring so the eye
          // catches it against the card.
          boxShadow: `0 0 0 4px ${tokens.brandTintSoft}`,
          transition:
            "background-color 120ms ease, color 120ms ease, box-shadow 120ms ease",
          "&:hover, &:focus-visible": {
            bgcolor: tokens.brand,
            color: "#fff",
            boxShadow: `0 0 0 5px ${tokens.brandTint}`,
          },
          "&:focus-visible": {
            outline: `2px solid ${tokens.brand}`,
            outlineOffset: 1,
          },
          "&:focus": { outline: "none" },
        }}
      >
        ?
      </Box>
    </Tooltip>
  );
}
